import { useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { Tooltip } from '@mui/material';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { toast } from 'sonner';
import { cn } from '@design-system';
import { MAX_IMAGES, MAX_IMAGE_SIZE_BYTES, ALLOWED_IMAGE_TYPES } from '@shared';
import { useSortableItem } from '@/features/quote-and-apply-builder/hooks/use-sortable-item';
import { Button } from './ui/button';
import { ArrowDown, ArrowUp, GripVertical, ImageIcon, X } from 'lucide-react';

const DEFAULT_MAX_IMAGE_SIZE_MB = MAX_IMAGE_SIZE_BYTES / (1024 * 1024);
const ACCEPTED_EXTENSIONS = '.jpg,.jpeg,.png';

/** Dedicated type so policy image rows never clash with form-builder / comparison DnD. */
const POLICY_UPLOAD_IMAGE_DND_TYPE = 'POLICY_UPLOAD_IMAGE_ROW';

type ImageFileUploaderProps = {
  files: File[];
  setFiles: (files: File[]) => void;
  className?: string;
  maxImages?: number;
  maxFileSizeMB?: number;
  disabled?: boolean;
};

const convertMBToBytes = (mb: number) => mb * 1024 * 1024;

function validateImageFile(file: File, maxFileSizeMB: number): string | null {
  if (!(ALLOWED_IMAGE_TYPES as readonly string[]).includes(file.type)) {
    return `${file.name}: Unsupported type. Please upload JPEG or PNG images.`;
  }
  if (file.size > convertMBToBytes(maxFileSizeMB)) {
    return `${file.name}: exceeds ${maxFileSizeMB}MB limit.`;
  }
  return null;
}

function fileRowKey(file: File, index: number) {
  return `${file.name}-${file.size}-${file.lastModified}-${index}`;
}

export function ImageFileUploader({
  files,
  setFiles,
  className,
  maxImages = MAX_IMAGES,
  maxFileSizeMB = DEFAULT_MAX_IMAGE_SIZE_MB,
  disabled = false,
}: ImageFileUploaderProps) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const filesRef = useRef(files);
  useEffect(() => {
    filesRef.current = files;
  }, [files]);

  const addFiles = (incoming: FileList) => {
    if (disabled) return;
    const validFiles: File[] = [];
    const errors: string[] = [];

    for (let i = 0; i < incoming.length; i++) {
      const f = incoming[i];
      const err = validateImageFile(f, maxFileSizeMB);
      if (err) {
        errors.push(err);
      } else {
        validFiles.push(f);
      }
    }

    if (errors.length > 0) {
      toast.error(errors.length === 1 ? errors[0]! : "Some images couldn't be added", {
        description: errors.length > 1 ? errors.join('\n') : undefined,
      });
    }

    const remaining = maxImages - files.length;
    if (remaining <= 0) {
      toast.warning(`Maximum ${maxImages} images allowed.`);
      return;
    }
    const toAdd = validFiles.slice(0, remaining);
    if (toAdd.length < validFiles.length) {
      toast.warning(`Only ${remaining} more image(s) can be added (max ${maxImages}).`);
    }
    if (toAdd.length > 0) {
      setFiles([...files, ...toAdd]);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      addFiles(e.target.files);
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (disabled) return;
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      addFiles(e.dataTransfer.files);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const removeFile = (index: number) => {
    setFiles(files.filter((_, i) => i !== index));
  };

  const moveFile = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= files.length) return;
    const next = [...files];
    [next[index], next[target]] = [next[target], next[index]];
    setFiles(next);
  };

  const reorderFiles = useCallback(
    (fromIndex: number, toIndex: number) => {
      if (fromIndex === toIndex) return;
      const prev = filesRef.current;
      const next = [...prev];
      const [removed] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, removed!);
      setFiles(next);
    },
    [setFiles],
  );

  const clearAll = () => setFiles([]);

  const hasFiles = files.length > 0;

  return (
    <div className={classNames('col-span-full', className)}>
      {!hasFiles && (
        <div
          className="flex justify-center rounded-lg border border-dashed border-gray-900/25 px-6 py-10"
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <div className="text-center">
            <ImageIcon
              aria-hidden="true"
              className="text-primary-500 mx-auto h-12 w-12"
            />
            <div className="mt-4 flex text-sm leading-6 text-gray-700">
              <label
                htmlFor="image-upload"
                className={cn(
                  'relative rounded-md bg-white font-semibold focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2',
                  disabled
                    ? 'pointer-events-none cursor-not-allowed text-gray-400'
                    : 'text-primary focus-within:ring-primary hover:text-primary-hover cursor-pointer',
                )}
              >
                <span>Upload images</span>
                <input
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  id="image-upload"
                  name="image-upload"
                  type="file"
                  accept={ACCEPTED_EXTENSIONS}
                  multiple
                  className="sr-only"
                  disabled={disabled}
                />
              </label>
              <p className="pl-1">or drag and drop</p>
            </div>
            <p className="text-xs leading-5 text-gray-700">
              JPEG or PNG, up to {maxFileSizeMB}MB each (max {maxImages} images)
            </p>
          </div>
        </div>
      )}

      {hasFiles && (
        <DndProvider backend={HTML5Backend}>
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">
                {files.length} image{files.length !== 1 ? 's' : ''} selected -{' '}
                <span className="md:hidden">Use arrows to reorder</span>
                <span className="hidden md:inline">Drag to reorder</span>
              </span>
              <div className="flex gap-2">
                {files.length < maxImages && (
                  <label
                    htmlFor="image-upload-more"
                    className={cn(
                      'text-sm font-medium',
                      disabled
                        ? 'pointer-events-none cursor-not-allowed text-gray-400'
                        : 'text-primary hover:text-primary-hover cursor-pointer',
                    )}
                  >
                    + Add more
                    <input
                      ref={fileInputRef}
                      onChange={handleFileChange}
                      id="image-upload-more"
                      type="file"
                      accept={ACCEPTED_EXTENSIONS}
                      multiple
                      className="sr-only"
                      disabled={disabled}
                    />
                  </label>
                )}
                <button
                  onClick={clearAll}
                  className="text-sm text-red-600 hover:text-red-800"
                  disabled={disabled}
                >
                  Remove all
                </button>
              </div>
            </div>
            <div className="flex flex-col gap-1">
              {files.map((file, index) => (
                <ImageThumbnailRow
                  key={fileRowKey(file, index)}
                  file={file}
                  index={index}
                  total={files.length}
                  sortableId={fileRowKey(file, index)}
                  onRemove={() => removeFile(index)}
                  onMoveUp={() => moveFile(index, -1)}
                  onMoveDown={() => moveFile(index, 1)}
                  onReorder={reorderFiles}
                  disabled={disabled}
                />
              ))}
            </div>
          </div>
        </DndProvider>
      )}
    </div>
  );
}

function ImageThumbnailRow({
  file,
  index,
  total,
  sortableId,
  onRemove,
  onMoveUp,
  onMoveDown,
  onReorder,
  disabled,
}: {
  file: File;
  index: number;
  total: number;
  sortableId: string;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onReorder: (fromIndex: number, toIndex: number) => void;
  disabled: boolean;
}) {
  const [thumbUrl, setThumbUrl] = useState<string | null>(null);
  useEffect(() => {
    const url = URL.createObjectURL(file);
    setThumbUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const { itemRef, handleRef, isDragging } = useSortableItem({
    type: POLICY_UPLOAD_IMAGE_DND_TYPE,
    id: sortableId,
    index,
    label: file.name,
    onMove: onReorder,
    disabled,
  });

  return (
    <div
      ref={itemRef}
      className={cn(
        'flex items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2',
        isDragging && 'border-dashed border-gray-400 opacity-60',
      )}
    >
      <Tooltip title="Drag to reorder">
        <div
          ref={handleRef}
          className={cn(
            'hidden shrink-0 touch-none md:flex md:items-center',
            disabled
              ? 'cursor-not-allowed text-gray-300'
              : 'cursor-grab text-gray-400 hover:text-gray-600 active:cursor-grabbing',
          )}
          aria-label="Drag to reorder"
        >
          <GripVertical className="h-4 w-4" />
        </div>
      </Tooltip>
      <span className="w-6 text-center text-xs font-semibold text-gray-500">{index + 1}</span>
      {thumbUrl ? (
        <img
          src={thumbUrl}
          alt={`Page ${index + 1}`}
          className="h-10 w-10 shrink-0 rounded object-cover"
        />
      ) : (
        <div className="h-10 w-10 shrink-0 rounded bg-gray-200" aria-hidden />
      )}
      <Tooltip title={file.name}>
        <span className="min-w-0 flex-1 truncate text-sm text-gray-700">{file.name}</span>
      </Tooltip>
      <span className="shrink-0 text-xs text-gray-400">{(file.size / 1024).toFixed(0)} KB</span>
      <div className="flex shrink-0 gap-1">
        <div
          className="flex gap-1 md:hidden"
          aria-label="Reorder images"
        >
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={onMoveUp}
            disabled={disabled || index === 0}
            aria-label="Move up"
          >
            <ArrowUp className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={onMoveDown}
            disabled={disabled || index === total - 1}
            aria-label="Move down"
          >
            <ArrowDown className="h-3.5 w-3.5" />
          </Button>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-red-500 hover:text-red-700"
          onClick={onRemove}
          disabled={disabled}
          aria-label="Remove image"
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}
